<script setup>
import Player from '@vimeo/player'
import usePlayerSettings from '@/Composables/usePlayerSettings'
import { ref, onMounted, watch } from 'vue'

const { state } = usePlayerSettings()

const props = defineProps({
    episode: Object,
    currentTime: Number,
})

const emit = defineEmits()

let player
const playerLoaded = ref(false)
const playerElement = ref(null)

onMounted(() => {
    playerLoaded.value = false

    player = new Player(playerElement.value, {
        id: props.episode.vimeo_id,
        byline: false,
        title: false,
        speed: true,
        responsive: true,
        playbackRate: state.value.speed,
        dnt: true,
        autoplay: true,
    })

    watch(() => props.episode.vimeo_id, () => {
        player.getPlaybackRate().then((rate) => {
            player.loadVideo(props.episode.vimeo_id).then(() => {
                player.setQuality(state.value.quality)
                player.setPlaybackRate(rate)
                player.setVolume(state.value.volume ?? 1)

                if (state.value.muted) {
                    player.setMuted(true)
                }

                if (state.value.textTrack) {
                    player.enableTextTrack(state.value.textTrack).catch(e => {})
                }
            })
        })
    })

    let lastEmittedProgress

    player.on('timeupdate', (event) => {
        let seconds = Math.floor(event.seconds)

        emit('progress', seconds)
        emit('rawProgress', event.seconds)

        if (lastEmittedProgress === seconds) {
            return
        }

        if (seconds === 0 || seconds % 10 === 0 || seconds === props.episode.duration_seconds) {
            emit('progress-interval', seconds)
            lastEmittedProgress = seconds
        }
    })

    player.on('ended', () => {
        emit('progress', props.episode.duration_seconds)
        emit('ended')
    })

    player.on('texttrackchange', (event) => {
        state.value.textTrack = event.language
    })

    player.on('qualitychange', (event) => {
        state.value.quality = event.quality
    })

    player.on('volumechange', (event) => {
        state.value.volume = event.volume

        player.getMuted().then((muted) => {
            state.value.muted = muted
        })
    })

    player.on('playbackratechange', (event) => {
        state.value.speed = event.playbackRate
    })

    player.ready().then(() => {
        if (props.currentTime) {
            player.setCurrentTime(props.currentTime)
        }

        player.setQuality(state.value.quality).then(() => {
            player.setPlaybackRate(state.value.speed)
            player.setVolume(state.value.volume ?? 1)

            if (state.value.muted) {
                player.setMuted(true)
            }

            if (state.value.textTrack) {
                player.enableTextTrack(state.value.textTrack).catch(e => {})
            }
        })
    })

    player.on('loaded', () => {
        playerLoaded.value = true
    })

    player.on('fullscreenchange', (event) => {
        emit('fullscreen', event.fullscreen)
    })
})

defineExpose({
    setCurrentTime: (time) => {
        player.setCurrentTime(time)
    }
})
</script>

<template>
    <div id="player" ref="playerElement" class="md:rounded-md" :class="{ 'player-container': !playerLoaded }"></div>
</template>
